import { useEffect, useRef, useState } from 'react';

import {
  parseRoxyCommand,
  stripRoxyWake,
  type RoxyCommand,
} from './parse-roxy-command.js';

const ARMED_MS = 8_000;

interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly 0?: { transcript?: string };
}

interface SpeechRecognitionEventLike {
  readonly resultIndex: number;
  readonly results: ArrayLike<SpeechRecognitionResultLike>;
}

interface BrowserSpeechRecognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: Event) => void) | null;
  onend: (() => void) | null;
}

function speechRecognitionCtor(): (new () => BrowserSpeechRecognition) | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const host = window as Window & {
    SpeechRecognition?: new () => BrowserSpeechRecognition;
    webkitSpeechRecognition?: new () => BrowserSpeechRecognition;
  };
  return host.SpeechRecognition ?? host.webkitSpeechRecognition ?? null;
}

export function isRoxySpeechSupported(): boolean {
  return speechRecognitionCtor() !== null;
}

export function useRoxyListener(input: {
  enabled: boolean;
  lang?: string;
  onCommand: (command: RoxyCommand) => void;
}): { supported: boolean; listening: boolean } {
  const onCommandRef = useRef(input.onCommand);
  onCommandRef.current = input.onCommand;
  const supported = isRoxySpeechSupported();
  const [listening, setListening] = useState(false);

  useEffect(() => {
    if (!input.enabled || !supported) {
      setListening(false);
      return;
    }
    const Ctor = speechRecognitionCtor();
    if (!Ctor) {
      return;
    }

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = input.lang ?? (typeof document !== 'undefined' ? document.documentElement.lang || 'en-US' : 'en-US');

    let armedUntil = 0;
    let stopped = false;

    recognition.onresult = (event) => {
      const result = event.results[event.resultIndex];
      const text = result?.[0]?.transcript?.replace(/\s+/g, ' ').trim();
      if (!result?.isFinal || !text) {
        return;
      }
      const stripped = stripRoxyWake(text);
      const now = Date.now();
      if (stripped.armed) {
        armedUntil = now + ARMED_MS;
        if (!stripped.rest) {
          return;
        }
        const command = parseRoxyCommand(stripped.rest);
        if (command) {
          armedUntil = 0;
          onCommandRef.current(command);
        }
        return;
      }
      if (now < armedUntil) {
        const command = parseRoxyCommand(stripped.rest);
        if (command) {
          armedUntil = 0;
          onCommandRef.current(command);
        }
      }
    };

    recognition.onerror = () => {
      /* restart onend */
    };
    recognition.onend = () => {
      setListening(false);
      if (!stopped) {
        try {
          recognition.start();
          setListening(true);
        } catch {
          /* already started */
        }
      }
    };

    try {
      recognition.start();
      setListening(true);
    } catch {
      setListening(false);
    }

    return () => {
      stopped = true;
      setListening(false);
      recognition.onend = null;
      recognition.onresult = null;
      recognition.abort();
    };
  }, [input.enabled, input.lang, supported]);

  return { supported, listening };
}
